import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewEncapsulation } from '@angular/core';
import { FormGroup, FormArray, ValidatorFn, FormGroupDirective, FormControl, Validators } from '@angular/forms';
import { GlobalService } from 'app/global/global.service';
import { CategoryService } from 'app/main/pages/category.service';
import { IDocumentTypesDto } from 'app/main/pages/documents/dto/document-listing.dto';
import { DocumentTypeService } from 'app/main/pages/settings/documenttype/document-type.service';
import { ICategory } from 'app/main/shared/shared/table/components/category-name/category-name.component';
import { FilesValidatorsService } from 'app/main/shared/validators/files-validators';
import { FileSizePipe } from 'app/pipes/file-size.pipe';
import { Subject, BehaviorSubject, take } from 'rxjs';
import {flattenCategories} from "../../../../../categories.utils";
import { IBulkOptionDLLItemsDTO } from '../match-contract-cards-step/match-contract-cards-step.component';
 

export interface IPDFMatching {
    filename:string;
    document_type_ids:number[];
    category_ids:number[];
    contract_control_ids:number[];
}

@Component({
  selector: 'app-bulk-upload-pdf-files-step',
  templateUrl: './bulk-upload-pdf-files-step.component.html',
  styleUrls: ['./bulk-upload-pdf-files-step.component.scss'],
  encapsulation: ViewEncapsulation.None,
  providers:[FileSizePipe]
  
})
export class BulkUploadPDFFilesStepComponent <P extends {document_type_id:number, category_ids:number[]}> implements OnInit,  OnDestroy {
  private untilDestroy$: Subject<void> = new Subject<void>();

  @Input() formArrayName: string ='files'
  @Input() acceptableFiles: string[] = ["application/pdf"];
  @Input() maxFileSize: number = 20 * 1024 * 1024;
  @Input() uploadRequestParams: P;
  @Input() section: string = 'document';
  @Input() pdfMatching: IPDFMatching[] = [];
  @Input() contractCards: IBulkOptionDLLItemsDTO[];
  @Output() contractCardsSearchChanged: EventEmitter<string> = new EventEmitter<string>();

  documentTypes$: BehaviorSubject<IDocumentTypesDto[]> = new BehaviorSubject<IDocumentTypesDto[]>([]);
  categories$: BehaviorSubject<ICategory[]> = new BehaviorSubject<ICategory[]>([]);
  flattenedCategories: Partial<ICategory>[] = [];

  form: FormGroup;
  get formFiles (){
     return this.form.controls[this.formArrayName] as FormArray;
  }

  set formFiles(formFiles : FormArray) {
      this.form.controls[this.formArrayName] = formFiles;
  }

  filesValidation: ValidatorFn[];

  constructor(
              private hostFrom: FormGroupDirective,
              private categoryService: CategoryService,
              private documentTypeService: DocumentTypeService,
              private filesValidatorsService:FilesValidatorsService,
              private globalService: GlobalService,
          ) {
  }

  ngOnInit(): void {
      this.filesValidation =[
          this.filesValidatorsService.maxFileSizeValidator(this.maxFileSize),
          this.filesValidatorsService.fileTypeValidator(this.acceptableFiles)
      ];
      this.form = this.hostFrom.form;
      this.fetchCategories();
      this.fetchDocumentTypes();
  }

  ngOnDestroy(): void {
      this.untilDestroy$.next();
      this.untilDestroy$.complete();
  }

  onFileDelete(index: number) {
      if (index > -1) {
          this.formFiles.removeAt(index);
      }
  }

  onFileDropped($event: FileList | Event) {
      let tempFilesList:File[] = [];

      if ($event instanceof Event) {
          tempFilesList = [
              ...(($event.target as HTMLInputElement).files) as unknown as File[]
          ];
      } else {
          tempFilesList = $event as unknown as File[];
      }
      for (let i = 0; i < tempFilesList.length; i++) {
          if (!this.hasAlreadyAdded(tempFilesList[i])) {
              this.formFiles.push( new FormGroup({
                  progress: new FormControl(0),
                  file: new FormControl(tempFilesList[i], this.filesValidation),
                  category_ids:new FormControl(this.matchCategoriesForFile(tempFilesList[i])),
                  document_type_id:new FormControl(this.matchDocTypeForFile(tempFilesList[i]),  [Validators.required]),
                  contract_card_ids:new FormControl(this.matchCCForFile(tempFilesList[i]),  [Validators.required]),
              }));
          }
      }
  }



  onCCSearchChanged(search: string){
    this.contractCardsSearchChanged.next(search);
  }


  private  matchCCForFile(file: File): number[] {
      const fileEntry = this.getPdfMatchingEntryForFile(file);
      if (fileEntry) {
          return fileEntry.contract_control_ids?.length > 0  ? fileEntry.contract_control_ids : null;
      }
      return null
  }
  
  private  matchDocTypeForFile(file: File): number {
      const fileEntry = this.getPdfMatchingEntryForFile(file);
      if (fileEntry) {

          return fileEntry?.document_type_ids?.length === 1 ? fileEntry.document_type_ids[0] : null;
      }

      return null
  }

  private matchCategoriesForFile(file: File): number[] {
      const fileEntry = this.getPdfMatchingEntryForFile(file);
      if (fileEntry) {
          return fileEntry.category_ids || [];
      }

      return [];
  }

  private getPdfMatchingEntryForFile(file: File): IPDFMatching {
      return this.pdfMatching?.find(x => x.filename === file.name) ?? null;
  }

  private hasAlreadyAdded(file: File): boolean {
      return this.formFiles?.value.some(x => this.compareFile(file, x.file));
  }

  private compareFile(fileA: File, fileB: File): boolean {
      return fileA.name === fileB.name && fileA.type === fileB.type && fileA.size === fileB.size;
  }
  
  private fetchCategories() {
      this.categoryService.fetchCategories('all')
          .pipe(take(1))
          .subscribe(x=> {
              this.flattenedCategories = flattenCategories(x as ICategory[]);
              this.categories$.next(x as ICategory[]);
          });
  }

  private fetchDocumentTypes() {
      this.documentTypeService.getDocumentTypes(1, '', 'all')
          .pipe(take(1))
          .subscribe(x=>this.documentTypes$.next(x as  IDocumentTypesDto[]));
  }
}
